import { Component } from "@angular/core";
import { ActivatedRoute, ParamMap } from "@angular/router";
import { PonderDataClient } from "@apiserver-rest/ponder-data-client";
import { AdvancedSearchRequest, ArtistSearch, GameType, ItemSearch, MagicSearch, SearchRequest, SearchResponse } from "@apiserver-rest/ponder-data-client-base";
import { EventBroker } from "@ponder-core/events/eventBroker";
import { LoadingComponent } from "@ponder-shared/loading/loading.component";
import { FilteredPreviewItems } from "@ponder-shared/tools/filter/filtered-preview-items";
import { getBoolean } from "@ponder-shared/utils/general";
import { debounceTime, Subscription, take } from "rxjs";
import { SearchInfoComponent } from "./search-info.component";
import { VirtualGridComponent } from "@ponder-shared/virtual/virtual-grid.component";
import { PreviewItemFilters } from "@ponder-shared/tools/filter/preview-item-filters";
import { AdvancedSearchService } from "./advanced/advanced-search.service";
import { isMobileScreen } from "@ponder-shared/utils/screenBreakpoints";
import { HttpClient } from "@angular/common/http";
import { environment } from "../../../environments/environment";
import { MagicAdvancedSearchFile } from "@apiserver-rest/ponder-tasks-client-base";

@Component({
    selector: "search",
    imports: [LoadingComponent, SearchInfoComponent, VirtualGridComponent],
    providers: [AdvancedSearchService],
    templateUrl: "./search.component.html"
})
export class SearchComponent {
    public isLoading: boolean = true;
    public loadingSuccess: boolean = false;

    public previewItems?: FilteredPreviewItems;
    public searchTerm: string = "";

    public searchParameters: string[] = [];
    public showAllParameters: boolean = false;
    
    private filterChangeSub?: Subscription;

    public constructor(
            private ponderDataClient: PonderDataClient,
            private route: ActivatedRoute,
            private events: EventBroker,
            private advancedSearch: AdvancedSearchService,
            private httpClient: HttpClient) {
        this.events.ui.navbarUpdateEvent.emit();
        this.filterChangeSub = this.events.ui.previewFilterChangeEvent
            .pipe(debounceTime(100))
            .subscribe({
                next: (filter: PreviewItemFilters) => {
                    this.onFilterUpdated(filter);
                }
        });
    }

    public ngOnDestroy() {
        this.filterChangeSub?.unsubscribe();
    }

    public ngAfterViewInit(): void {
        if (this.route.snapshot.queryParamMap.has("advanced") && (this.route.snapshot.queryParamMap.get("advanced")!.toLowerCase() == "true")) {
            this.processAdvancedSearchRequest();
        } else {
            this.processSimpleSearchRequest();
        }
    }
    
    private onFilterUpdated(itemFilter: PreviewItemFilters) {
        this.previewItems?.populate(itemFilter);
        window.scroll(0, 0);
    }

    private processSimpleSearchRequest(): void {
        var searchRequest = this.buildRequestFromQueryParams();

        this.ponderDataClient
            .search(searchRequest)
            .pipe(take(1))
            .subscribe({
                next: (response: SearchResponse) => {
                    if (response.success) {
                        this.loadingSuccess = true;
                        
                        if (response.results) {
                            this.previewItems = new FilteredPreviewItems(response.results, this.events);
                            this.previewItems.populate();
                            
                            this.events.ui.toggleSortFilterBarEvent.emit(true);
                            this.events.ui.previewItemsFetchedEvent.emit(this.previewItems);
                        } else {
                            this.previewItems = new FilteredPreviewItems([], this.events);
                            this.previewItems.populate();
                        }
                    }
                }
            })
            .add(() => { this.isLoading = false; });
    }

    private processAdvancedSearchRequest(): void {
        this.advancedSearch.fromQueryString(this.route.snapshot.queryParamMap);

        this.httpClient.get(`${environment.ponderDataBaseUrl}/advanced-search/Magic.json`)
            .pipe(take(1))
            .subscribe({
                next: (response: Object) => {
                    this.searchParameters = this.advancedSearch.compileParametersList(response as MagicAdvancedSearchFile);
                }
            })
            .add(() => { this.isLoading = false });

        const request = new AdvancedSearchRequest({
            start: 0,
            limit: 1000,
            magicParameters: this.advancedSearch.parameters
        });

        this.ponderDataClient
            .searchAdvanced(request)
            .pipe(take(1))
            .subscribe({
                next: (response: SearchResponse) => {
                    if (response.success) {
                        this.loadingSuccess = true;
                        
                        if (response.results) {
                            this.previewItems = new FilteredPreviewItems(response.results, this.events);
                            this.previewItems.populate();
                            
                            this.events.ui.toggleSortFilterBarEvent.emit(true);
                            this.events.ui.previewItemsFetchedEvent.emit(this.previewItems);
                        } else {
                            this.previewItems = new FilteredPreviewItems([], this.events);
                            this.previewItems.populate();
                        }
                    }
                },
                error: (err: any) => {
                    console.error("uh oh?");
                    console.error(JSON.stringify(err));
                }
            })
            .add(() => { this.isLoading = false; });
    }

    public isMobileScreen(): boolean {
        return isMobileScreen();
    }
    
    // -------------------------------------------------------------------------------------
    // Geneal Search
    // -------------------------------------------------------------------------------------

    private buildRequestFromQueryParams(): SearchRequest | undefined {
        var queryParams = this.route.snapshot.queryParamMap;

        // Required to include "game" parameter
        if (!queryParams.has("game")) {
            return undefined;
        }

        var request = new SearchRequest({
            game: queryParams.get("game") as GameType,
            item: this.buildItemSearchFromQueryParams(queryParams),
            artist: this.buildArtistSearchFromQueryParams(queryParams)
        });

        if (request.game == GameType.Magic) {
            request.magic = this.buildMagicSearchFromQueryParams(queryParams);
        }
        
        return request;
    }

    private buildItemSearchFromQueryParams(queryParams: ParamMap): ItemSearch | undefined {
        var name = queryParams.get("name");
        var isExact = getBoolean(queryParams.get("exact"));

        if (!name) {
            return undefined;
        }

        this.searchTerm = `name ${(isExact ? "is" : "like")} "${name}"`;

        return new ItemSearch({
            name: name,
            exact: isExact
        });
    }

    private buildArtistSearchFromQueryParams(queryParams: ParamMap): ArtistSearch | undefined {
        var artist = queryParams.get("artist");

        if (!artist) {
            return undefined;
        }
        
        this.searchTerm = `artist "${artist}"`;

        return new ArtistSearch({
            name: artist
        });
    }

    // -------------------------------------------------------------------------------------
    // Magic-Specific Search
    // -------------------------------------------------------------------------------------

    private buildMagicSearchFromQueryParams(queryParams: ParamMap): MagicSearch | undefined {
        var sets = this.buildMagicSetSearch(queryParams);
        var oracleId = this.buildMagicOracleIdSearch(queryParams);
        var scryfallId = this.buildMagicScryfallIdSearch(queryParams);

        if (sets) {
            return new MagicSearch({ sets: sets });
        } else if (oracleId) {
            return new MagicSearch({ oracleId: oracleId });
        } else if (scryfallId) {
            return new MagicSearch({ scryfallId: scryfallId });
        }

        return undefined;
    }

    private buildMagicSetSearch(queryParams: ParamMap): string[] | undefined {
        var set = queryParams.get("set");

        if (!set) {
            return undefined;
        }

        this.searchTerm = `set "${set}"`;

        return set.split(",");
    }

    private buildMagicOracleIdSearch(queryParams: ParamMap): string | undefined {
        var oracleId = queryParams.get("oracleId");

        if (!oracleId) {
            return undefined;
        }

        this.searchTerm = `oracle id "${oracleId}"`;

        return oracleId;
    }

    private buildMagicScryfallIdSearch(queryParams: ParamMap): string | undefined {
        var scryfallId = queryParams.get("scryfallId");

        if (!scryfallId) {
            return undefined;
        }

        this.searchTerm = `scryfall id "${scryfallId}"`;

        return scryfallId;
    }
}